import { useCallback, useEffect, useState } from 'react'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import {
  hasOpenPublication,
  hasPublicationFailed,
  isRetryExhausted,
  PUBLICATION_POLL_INTERVAL_MS,
} from '../config/brandLifecycleConfig'
import { brandExtractionService as sharedExtractionService } from '../services/brandExtractionService'

// Everything after Send to Publish Queue. GET /publications/current is the
// word on whether the brand is already queued, failed or out of retries, and
// it is re-read whenever the brand is — so every action here just has the
// caller refresh the brand afterwards.
//
// `onPublicationChange` resolves once the brand has been re-read. It is called
// when a queued publication moves on by itself, since going live shows up on
// the brand rather than here.
export const useBrandPublish = ({
  brand,
  extractionService,
  isEnabled = false,
  onPublicationChange,
}) => {
  const service = extractionService || sharedExtractionService
  const [isPublishing, setIsPublishing] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [currentPublication, setCurrentPublication] = useState(null)
  const [isCheckingPublication, setIsCheckingPublication] = useState(false)
  const [publicationCheckFailed, setPublicationCheckFailed] = useState(false)

  useEffect(() => {
    let isCurrent = true
    if (!isEnabled) {
      setCurrentPublication(null)
      setIsCheckingPublication(false)
      setPublicationCheckFailed(false)
      return () => {
        isCurrent = false
      }
    }

    // The last answer is kept while this one is in flight, so the rail does
    // not drop back to Send to Publish Queue on every re-read.
    setIsCheckingPublication(true)
    setPublicationCheckFailed(false)
    service
      .getCurrentPublication(brand)
      .then((publication) => {
        if (isCurrent) setCurrentPublication(publication)
      })
      .catch((requestError) => {
        if (!isCurrent) return
        // 404 is a brand that has never been sent, not a failed check.
        if (requestError?.status === 404) setCurrentPublication(null)
        else setPublicationCheckFailed(true)
      })
      .finally(() => {
        if (isCurrent) setIsCheckingPublication(false)
      })

    return () => {
      isCurrent = false
    }
  }, [brand, isEnabled, service])

  // Polled only while waiting in the queue. A failed check is skipped; the
  // next one tries again.
  const queuedStatus =
    hasOpenPublication(currentPublication) &&
    !hasPublicationFailed(currentPublication)
      ? currentPublication.status
      : ''
  useEffect(() => {
    if (!isEnabled || !queuedStatus) return undefined
    let isCurrent = true
    const timer = setInterval(() => {
      service
        .getCurrentPublication(brand)
        .then((publication) => {
          if (!isCurrent) return
          setCurrentPublication(publication)
          if (publication?.status !== queuedStatus) {
            onPublicationChange().catch(() => {})
          }
        })
        .catch(() => {})
    }, PUBLICATION_POLL_INTERVAL_MS)
    return () => {
      isCurrent = false
      clearInterval(timer)
    }
  }, [brand, isEnabled, onPublicationChange, queuedStatus, service])

  // Resolves to {} or { error }, so the caller reads the message in the same
  // tick it decides what to say.
  const retryPublication = useCallback(async () => {
    setIsPublishing(true)
    try {
      await service.retryPublication(brand, currentPublication?.publication_id)
      return {}
    } catch (requestError) {
      return {
        error: getApiErrorMessage(
          requestError,
          'The brand could not be published.',
        ),
      }
    } finally {
      setIsPublishing(false)
    }
  }, [brand, currentPublication, service])

  const cancelPublication = useCallback(
    async (publicationId) => {
      setIsCancelling(true)
      try {
        await service.cancelPublication(brand, publicationId)
        return {}
      } catch (requestError) {
        return {
          error: getApiErrorMessage(
            requestError,
            'Publishing could not be cancelled.',
          ),
        }
      } finally {
        setIsCancelling(false)
      }
    },
    [brand, service],
  )

  const recoverPublication = useCallback(async () => {
    setIsPublishing(true)
    try {
      await service.recoverPublication(
        brand,
        currentPublication?.publication_id,
      )
      return {}
    } catch (requestError) {
      return {
        error: getApiErrorMessage(
          requestError,
          'The publication could not be recovered.',
        ),
      }
    } finally {
      setIsPublishing(false)
    }
  }, [brand, currentPublication, service])

  return {
    cancelPublication,
    currentPublication,
    isCancelling,
    isCheckingPublication,
    isPublishing,
    isRetryExhausted: isRetryExhausted(currentPublication),
    publicationCheckFailed,
    recoverPublication,
    retryPublication,
  }
}
